import { useCallback, useEffect, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import BulkUploadModal from "../../../components/ui/BulkUploadModal";
import SuccessView from "../../../components/ui/SuccessView";
import { BulkUploadField } from "../../../lib/bulkUpload";
import { useCreateFineMutation } from "../fineApiSlice";

const FINE_CATEGORIES = [
  "Compliance And Regulaory",
  "Safety And Driving Behavior",
  "Cargo and Delivery Related Fines",
  "Company Policy Violations",
  "Vehicle Conditions and Maintenance",
] as const;

const FINE_BULK_FIELDS: BulkUploadField[] = [
  {
    key: "name",
    label: "Fine name",
    required: true,
    defaultColumnName: "Fine name",
    example: "Name, Title",
  },
  {
    key: "amount",
    label: "Amount",
    required: true,
    type: "number",
    defaultColumnName: "Amount",
  },
  {
    key: "category",
    label: "Category",
    required: true,
    defaultColumnName: "Category",
    hint: FINE_CATEGORIES.join(", "),
  },
];

interface FineBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const normalize = (value: string) => value.trim().toLowerCase();

const resolveCategory = (value: string) => {
  const needle = normalize(value);
  return (
    FINE_CATEGORIES.find((category) => normalize(category) === needle) ||
    FINE_CATEGORIES.find(
      (category) =>
        normalize(category).includes(needle) ||
        needle.includes(normalize(category)),
    )
  );
};

const FineBulkUploadModal: React.FC<FineBulkUploadModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [createFine] = useCreateFineMutation();

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setUploadedCount(0);
    }
  }, [isOpen]);

  const validateRecord = useCallback((record: Record<string, unknown>) => {
    const errors: string[] = [];
    const category = String(record.category ?? "");
    if (category && !resolveCategory(category)) {
      errors.push(`Unknown category "${category}"`);
    }
    const amount = Number(record.amount);
    if (record.amount != null && record.amount !== "" && Number.isNaN(amount)) {
      errors.push("Amount must be a number");
    }
    if (!Number.isNaN(amount) && amount < 0) {
      errors.push("Amount cannot be negative");
    }
    return errors;
  }, []);

  const handleUploadRow = async (row: Record<string, unknown>) => {
    const payload = {
      name: row.name,
      amount: Number(row.amount),
      category: resolveCategory(String(row.category ?? "")) || row.category,
    };

    try {
      await createFine(payload).unwrap();
    } catch (error) {
      const message =
        getErrorDetails(error).message || "Failed to add this fine.";
      throw new Error(message);
    }
  };

  const handleUploadComplete = (result: {
    succeeded: number;
    failed: number;
  }) => {
    if (result.failed === 0 && result.succeeded > 0) {
      setUploadedCount(result.succeeded);
      setShowSuccess(true);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setUploadedCount(0);
    onClose();
  };

  if (showSuccess) {
    return (
      <BulkUploadSuccess
        isOpen={isOpen}
        count={uploadedCount}
        onClose={handleClose}
      />
    );
  }

  return (
    <BulkUploadModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk upload fines"
      entityLabel="fines"
      fields={FINE_BULK_FIELDS}
      storageKey="fines"
      templateFileName="fine-bulk-upload-template"
      onUploadRow={handleUploadRow}
      onUploadComplete={handleUploadComplete}
      getRowLabel={(row) =>
        [row.name, row.category, row.amount].filter(Boolean).join(" · ")
      }
      validateRecord={validateRecord}
    />
  );
};

const BulkUploadSuccess: React.FC<{
  isOpen: boolean;
  count: number;
  onClose: () => void;
}> = ({ isOpen, count, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed w-full h-full inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full mx-4 overflow-hidden">
        <SuccessView
          handleCloseModal={onClose}
          heading=""
          message={`${count} ${
            count === 1 ? "fine has" : "fines have"
          } been uploaded successfully.`}
        />
      </div>
    </div>
  );
};

export default FineBulkUploadModal;
